import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { menuSize, placeAnchoredMenu } from '../../utils/menuPlacement'

export default function ScanMenu({
  label,
  ariaLabel,
  items = [],
  className = '',
  triggerClassName = 'scan-menu-trigger',
  align = 'start',
}) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 220, maxHeight: 240, ready: false })
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const visibleItems = items.filter(Boolean)

  useLayoutEffect(() => {
    if (!open) return undefined
    const update = () => {
      const trigger = triggerRef.current?.getBoundingClientRect()
      if (!trigger) return
      const size = menuSize(menuRef.current)
      const width = Math.min(
        Math.max(8, window.innerWidth - 16),
        Math.max(align === 'end' ? 220 : trigger.width, size.width, 180),
      )
      const placed = placeAnchoredMenu({
        trigger,
        width,
        height: size.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
        align,
        prefer: 'below',
      })
      setCoords((prev) => (
        prev.left === placed.left
        && prev.top === placed.top
        && prev.width === width
        && prev.maxHeight === placed.maxHeight
        && prev.ready
          ? prev
          : { left: placed.left, top: placed.top, width, maxHeight: placed.maxHeight, ready: true }
      ))
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, align, visibleItems.length])

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (event) => {
      if (rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      if (event.key !== 'Tab' || !menuRef.current) return
      const nodes = [...menuRef.current.querySelectorAll('button:not([disabled])')]
      if (!nodes.length) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  useEffect(() => {
    if (!open || !coords.ready) return undefined
    const first = menuRef.current?.querySelector('button:not([disabled])')
    first?.focus({ preventScroll: true })
    return undefined
  }, [open, coords.ready])

  useEffect(() => {
    if (!open) setCoords((prev) => (prev.ready ? { ...prev, ready: false } : prev))
  }, [open])

  return (
    <div className={`scan-menu ${className}`.trim()} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {label}
      </button>
      {open && typeof document !== 'undefined' ? createPortal(
        <div
          ref={menuRef}
          className="scan-menu-list scan-menu-portal"
          role="menu"
          style={{
            left: coords.left,
            top: coords.top,
            width: coords.width,
            maxHeight: coords.maxHeight,
            visibility: coords.ready ? 'visible' : 'hidden',
          }}
        >
          {visibleItems.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false)
                triggerRef.current?.focus()
                item.onClick?.()
              }}
            >
              {item.icon ? <span className="scan-menu-icon" aria-hidden="true">{item.icon}</span> : null}
              <span>{item.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </div>
  )
}
